import { useMemo, useState } from 'react';
import {
  ADMIN_LIST_LIMIT,
  type AdminIslandRow,
  type OrganizationSummary
} from 'minka-shared';
import {
  Button,
  DataTable,
  Input,
  Modal,
  SearchInput,
  SearchableSelect,
  TabSwitch,
  type DataTableColumn
} from '@/components/common';
import { IslandMembersPanel } from '@/components/room/IslandMembersPanel';
import { useAdminIslands } from '@/hooks/useAdminIslands';
import { useAdminOrganizations } from '@/hooks/useAdminOrganizations';
import { usePresence } from '@/hooks/usePresence';
import { countOf } from '@/utils/plural';
import {
  FaBuilding,
  FaCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTrash,
  FaUsers
} from 'react-icons/fa';

type IslandModalTab = 'members' | 'settings';

/** "1 island", not "1 islands": the group headers already say it this way. */
function islandCountLabel(count: number): string {
  return countOf(count, 'island');
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg flex items-center gap-2">
      <FaExclamationTriangle className="text-game-golden flex-shrink-0 text-xs" />
      <span className="font-medium text-sm">{message}</span>
    </div>
  );
}

interface IslandsTabProps {
  /**
   * Whether the first presence snapshot may be taken yet. False while the
   * console, entered from in-game, is still leaving the island (§6.7).
   */
  presenceReady?: boolean;
}

/**
 * The Islands tab of §4.3: every island, grouped by the organization that owns
 * it, with the live count from the presence snapshot.
 *
 * Organizations are loaded in their own right rather than derived from the
 * islands: one that owns no island has no island row to be derived from, and it
 * is exactly the one a superadmin may delete (§6.6).
 */
export function IslandsTab({ presenceReady = true }: IslandsTabProps) {
  const [search, setSearch] = useState('');
  const islands = useAdminIslands(search);
  // Unsearched: the organizations are the group headers and the move picker,
  // not results. Searching them too would strip a matching island of its
  // header's Rename and Delete, and leave the picker unable to offer the very
  // organizations the operator is moving the island away from.
  const organizations = useAdminOrganizations('');
  const presence = usePresence(presenceReady);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<IslandModalTab>('members');
  const [moveTarget, setMoveTarget] = useState<OrganizationSummary | null>(null);
  const [isDeletingIsland, setIsDeletingIsland] = useState(false);

  const [renameTarget, setRenameTarget] = useState<OrganizationSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteOrgTarget, setDeleteOrgTarget] = useState<OrganizationSummary | null>(null);

  // The open island is read out of the current rows rather than kept as a
  // snapshot, so a move or an organization rename is reflected in the modal
  // that caused it instead of leaving it showing the old name.
  const selected = useMemo(
    () => islands.data.rows.find(island => island.id === selectedId) ?? null,
    [islands.data, selectedId]
  );

  const organizationsById = useMemo(() => {
    const index = new Map<string, OrganizationSummary>();
    for (const organization of organizations.data.rows) {
      index.set(organization.id, organization);
    }
    return index;
  }, [organizations.data]);

  /**
   * The groups to render, in name order: every organization that either owns
   * one of the islands the search turned up, or whose own name matches the
   * search — so an organization with no islands is still findable by name,
   * which is the only way to reach the button that deletes it.
   */
  const groupKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    const withMatchingIsland = new Set(islands.data.rows.map(island => island.organization.id));

    return [...organizations.data.rows]
      .filter(
        organization =>
          withMatchingIsland.has(organization.id) ||
          term === '' ||
          organization.name.toLowerCase().includes(term)
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(organization => organization.id);
  }, [organizations.data, islands.data, search]);

  const openIsland = (island: AdminIslandRow) => {
    setSelectedId(island.id);
    setModalTab('members');
    setMoveTarget(null);
    setIsDeletingIsland(false);
    islands.clearError();
  };

  const confirmMove = async () => {
    if (!selected || !moveTarget) return;

    try {
      await islands.moveIsland(selected.id, moveTarget.id);
      setMoveTarget(null);
      // Membership follows the island, so the organization counts move with it.
      await organizations.refresh();
    } catch {
      // The hook has already phrased the refusal; the panel shows it.
    }
  };

  const confirmDeleteIsland = async () => {
    if (!selected) return;

    try {
      await islands.deleteIsland(selected.id);
      setSelectedId(null);
      await organizations.refresh();
    } catch {
      // Same: the banner explains it and the modal stays open.
    }
  };

  const submitRename = async () => {
    if (!renameTarget) return;

    try {
      await organizations.rename(renameTarget.id, renameValue.trim());
      setRenameTarget(null);
      // Island rows carry the organization's name, so they have to be re-read.
      await islands.refresh();
    } catch {
      // The dialog keeps the banner and the typed name.
    }
  };

  const confirmDeleteOrganization = async () => {
    if (!deleteOrgTarget) return;

    try {
      await organizations.remove(deleteOrgTarget.id);
      setDeleteOrgTarget(null);
    } catch {
      // 409 `organization_has_islands` is the one that matters here.
    }
  };

  const columns: Array<DataTableColumn<AdminIslandRow>> = [
    {
      key: 'name',
      header: 'Island',
      render: row => <span className="text-white font-medium">{row.name}</span>
    },
    { key: 'admins', header: 'Admins', render: row => row.adminCount },
    { key: 'residents', header: 'Residents', render: row => row.residentCount },
    { key: 'guests', header: 'Guests', render: row => row.guestCount },
    {
      key: 'live',
      header: 'Live',
      render: row => {
        const count = presence.islandCount(row.id);
        return count === 0 ? (
          <span className="text-gray-500">—</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-gray-200">
            <FaCircle className="text-[0.5rem] text-game-teal" />
            {count}
          </span>
        );
      }
    },
    { key: 'created', header: 'Created', render: row => formatDate(row.createdAt) }
  ];

  const renderGroupHeader = (organizationId: string, rows: AdminIslandRow[]) => {
    const organization = organizationsById.get(organizationId);
    const name = organization?.name ?? rows[0]?.organization.name ?? 'Unknown organization';
    // The server's count is authoritative: the rows here are only the ones the
    // current search turned up, which is not the same as "this org is empty".
    const activeIslandCount = organization?.activeIslandCount ?? rows.length;

    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FaBuilding className="text-game-golden text-sm" />
          <span className="text-white font-semibold">{name}</span>
          <span className="text-gray-400 text-xs">{islandCountLabel(activeIslandCount)}</span>
        </div>
        {organization && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              className="px-3 py-1 text-sm"
              onClick={() => {
                setRenameTarget(organization);
                setRenameValue(organization.name);
              }}
              title="Rename this organization"
            >
              Rename
            </Button>
            {/* Deleting is only offered once the organization is empty; the
                server refuses otherwise (409 `organization_has_islands`). */}
            {activeIslandCount === 0 && (
              <Button
                variant="danger"
                className="px-3 py-1 text-sm"
                onClick={() => setDeleteOrgTarget(organization)}
                title="Delete this empty organization"
              >
                <FaTrash className="text-xs" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          onChange={setSearch}
          placeholder="Search islands and organizations"
          ariaLabel="Search islands"
          className="flex-1 min-w-[16rem]"
        />
        <Button
          variant="secondary"
          onClick={() => void presence.refresh()}
          isLoading={presence.isLoading}
          title="Reload the presence snapshot"
        >
          <FaSyncAlt className="text-sm" />
          Refresh presence
        </Button>
      </div>

      {islands.error && <ErrorBanner message={islands.error} />}
      {organizations.error && <ErrorBanner message={organizations.error} />}
      {presence.error && <ErrorBanner message={presence.error} />}

      {islands.isLoading && islands.data.rows.length === 0 ? (
        <p className="text-gray-400 italic text-center py-6">Loading islands…</p>
      ) : (
        <DataTable
          columns={columns}
          rows={islands.data.rows}
          getRowKey={row => row.id}
          onRowClick={openIsland}
          groupBy={row => row.organization.id}
          groupKeys={groupKeys}
          renderGroupHeader={renderGroupHeader}
          emptyText={search ? 'No island matches that search.' : 'There are no islands yet.'}
        />
      )}

      {islands.data.truncated && (
        <p className="text-gray-400 text-sm italic">
          Showing the first {ADMIN_LIST_LIMIT} islands — refine your search.
        </p>
      )}

      <Modal
        isOpen={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected?.name ?? ''}
        size="2xl"
      >
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <FaBuilding className="text-game-golden text-xs" />
              {selected.organization.name}
              <span className="text-gray-600">·</span>
              <FaUsers className="text-xs" />
              {selected.adminCount + selected.residentCount + selected.guestCount} members
              <span className="text-gray-600">·</span>
              created by {selected.creatorUsername}
            </div>

            <div className="flex gap-2 p-1 bg-game-medium/20 rounded-lg">
              <TabSwitch
                isActive={modalTab === 'members'}
                onClick={() => setModalTab('members')}
              >
                Members &amp; invitations
              </TabSwitch>
              <TabSwitch isActive={modalTab === 'settings'} onClick={() => setModalTab('settings')}>
                Settings
              </TabSwitch>
            </div>

            {modalTab === 'members' ? (
              <IslandMembersPanel roomId={selected.id} mode="superadmin" />
            ) : (
              <div className="space-y-6">
                {islands.error && <ErrorBanner message={islands.error} />}

                <div className="space-y-3">
                  <h4 className="text-white font-semibold">Organization</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Moving an island moves its members' organization with it. Only a superadmin can
                    do this.
                  </p>

                  {moveTarget ? (
                    <div className="p-4 bg-game-medium/20 border border-game-medium/40 rounded-lg space-y-3">
                      <p className="text-gray-200 text-sm">
                        Move <strong className="text-white">{selected.name}</strong> to{' '}
                        <strong className="text-white">{moveTarget.name}</strong>?
                      </p>
                      <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => setMoveTarget(null)}>
                          Cancel
                        </Button>
                        <Button variant="primary" onClick={() => void confirmMove()}>
                          Move island
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FaBuilding className="text-game-golden text-xs flex-shrink-0" />
                      {/* Searchable: the list of organizations is unbounded, and
                          scrolling one to find a name is not a choice (#64). */}
                      <SearchableSelect
                        className="max-w-sm flex-1"
                        ariaLabel="Organization"
                        placeholder="Search organizations…"
                        emptyText="No organization matches that name"
                        // The island's own organization names the field but is
                        // never one of the targets: moving it there is not a
                        // move.
                        value={selected.organization.id}
                        valueLabel={selected.organization.name}
                        options={organizations.data.rows
                          .filter(organization => organization.id !== selected.organization.id)
                          .map(organization => ({
                            id: organization.id,
                            label: organization.name,
                            hint: islandCountLabel(organization.activeIslandCount)
                          }))}
                        onSelect={organizationId => {
                          const organization = organizationsById.get(organizationId);
                          if (organization) {
                            setMoveTarget(organization);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-3 pt-4 border-t border-game-medium/30">
                  <h4 className="text-white font-semibold">Delete island</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Everyone standing on it is sent back to their island list and every role on it
                    stops counting. Chat history is kept.
                  </p>

                  {isDeletingIsland ? (
                    <div className="p-4 bg-game-orange/20 border border-game-orange/50 rounded-lg space-y-3">
                      <p className="text-game-accent text-sm">
                        Delete <strong className="text-white">{selected.name}</strong>? This cannot
                        be undone from the console.
                      </p>
                      <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={() => setIsDeletingIsland(false)}>
                          Cancel
                        </Button>
                        <Button variant="danger" onClick={() => void confirmDeleteIsland()}>
                          Delete island
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="danger"
                      className="px-3 py-2 text-sm"
                      onClick={() => setIsDeletingIsland(true)}
                    >
                      <FaTrash className="text-xs" />
                      Delete island
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        title="Rename organization"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Organization name"
            name="organizationName"
            value={renameValue}
            onChange={event => setRenameValue(event.target.value)}
          />
          {organizations.error && <ErrorBanner message={organizations.error} />}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitRename()}
              disabled={renameValue.trim() === ''}
            >
              Rename
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteOrgTarget !== null}
        onClose={() => setDeleteOrgTarget(null)}
        title="Delete organization"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-300">
            Delete <strong className="text-white">{deleteOrgTarget?.name}</strong>?
          </p>
          <p className="text-gray-400 text-sm leading-relaxed">
            It owns no island, so nobody loses anything. An organization with islands has to have
            them moved or deleted first.
          </p>
          {organizations.error && <ErrorBanner message={organizations.error} />}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteOrgTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void confirmDeleteOrganization()}>
              Delete organization
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
